import { ChangeEvent, useEffect, useRef, useState } from 'react'

import slugify from '@sindresorhus/slugify'
import {
  Error,
  FieldLoading,
  InputSplit,
  ModalDialog,
  Notification,
  NotificationType,
  PrimaryButton,
  SecondaryButton,
  Success
} from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useFetcher, useRouteLoaderData } from 'react-router'
import { object, string } from 'zod'

import GatingInfoBox from './GatingInfoBox'
import { SelectTemplate } from './SelectTemplate'
import { usePortalUrl } from '~/portal/hooks/usePortalUrl'
import appDefaults from '~/portal/portal.defaults.json'
import { ProviderRootLoaderData } from '~/portal/routes/sp-admin/loader'
import { productNameSchema, slugSchema } from '~/portal/schemas/schema.shared'
import { ACTION } from '~/portal/services/actions'
import { ConsumerLoaderData } from '~/portal/types/portal'
import { ProductNameValidator, ProductSlugValidator } from '~/portal/types/product'
import Form from '~/shared/components/Form'
import { BaseProduct } from '~/shared/types/product'

const CreateProductModal = ({
  isOpen,
  onClose,
  className,
  initialTemplateId,
  duplicatedProduct,
}: {
  isOpen: boolean,
  onClose: () => void,
  className?: string,
  initialTemplateId?: string,
  duplicatedProduct?: BaseProduct,
}) => {
  const { t } = useTranslation()

  const adminLoaderData = useRouteLoaderData<ProviderRootLoaderData>('routes/sp-admin/_index')
  const consumerLoaderData = useRouteLoaderData<ConsumerLoaderData>('routes/_docs/_index')

  const { templates: { items: templatesItems = [] } = {} } = adminLoaderData || consumerLoaderData || {}

  const fetcher = useFetcher()
  const nameFetcher = useFetcher<ProductNameValidator>({ key: 'create-product-name' })
  const slugFetcher = useFetcher<ProductSlugValidator>({ key: 'create-product-slug' })
  const [nameExists, setNameExists] = useState(false)
  const [slugStatus, setSlugStatus] = useState<{ exists?: boolean; loading?: boolean; reserved?: boolean; }>()
  const [isCreating, setIsCreating] = useState(false)
  const [templateId, setTemplateId] = useState<string | undefined>(initialTemplateId)
  const [disabled, setDisabled] = useState(false)
  const slugManuallyEdited = useRef(false)

  const { hostname } = usePortalUrl()
  const { name, slug, id } = duplicatedProduct || {}

  const defaultProductName = duplicatedProduct ? `${name} copy` : ''
  const defaultProductSlug = duplicatedProduct ? `${slug}_copy` : ''

  useEffect(() => {
    if (fetcher.state === 'submitting' || fetcher.state === 'loading') {
      if (fetcher.formAction === ACTION.ADD_PRODUCT() || id && fetcher.formAction === ACTION.DUPLICATE_PRODUCT({ duplicatedProductId: id })) {
        setIsCreating(true)
      }
    } else {
      if (isCreating) {
        setIsCreating(false)
        onClose()
      }
    }

  }, [fetcher])

  useEffect(() => {
    if (nameFetcher.data && nameFetcher.state === 'idle') {
      setNameExists(nameFetcher.data.nameExists)
      setDisabled(false)
    }
  }, [nameFetcher.data, nameFetcher.state])

  useEffect(() => {
    if (slugFetcher.data && slugFetcher.state === 'idle') {
      setSlugStatus({ exists: slugFetcher.data.slugExists, loading: false, reserved: slugFetcher.data.slugReserved })
      setDisabled(false)
    }
  }, [slugFetcher.data, slugFetcher.state])

  useEffect(() => {
    if (duplicatedProduct) {
      const productName = encodeURIComponent(defaultProductName)
      setSlugStatus({ exists: false, loading: true })
      nameFetcher.load(`/services/api/product?productname=${productName}`)
      slugFetcher.load(`/services/api/product?productslug=${defaultProductSlug}`)
    }
  }, [duplicatedProduct, name, slug])

  const schema = object({
    productName: productNameSchema(),
    productSlug: slugSchema(),
    templateId: string().optional()
  })

  const getFetcherError = () => {
    if (slugStatus?.exists === undefined) {
      return null
    }
    if (slugStatus?.loading) {
      return <FieldLoading>{t('product.settings.tabs.general.sections.productSlug.edit.loading')}</FieldLoading>
    }
    if (slugStatus?.exists) {
      return <Error>{t('errors.components.ui.forms.input.text.duplicatedSlug')}</Error>
    }
    if (slugStatus?.reserved) {
      return <Error>{t('errors.components.ui.forms.input.text.reservedSlug')}</Error>
    }
    return <Success>{t('product.settings.tabs.general.sections.productSlug.edit.success')}</Success>
  }

  if (!isOpen) {
    return null
  }

  const isDefaultValueValid = (value: string, defaultValue: string, maxLength: number) => {
    if (value === defaultValue) {
      return value.length <= maxLength
    }
    return true
  }

  const loading = fetcher.state === 'loading' || fetcher.state === 'submitting'

  const createNewProductMode = !duplicatedProduct

  return (

    <ModalDialog
      loading={loading}
      isOpen={isOpen}
      onClose={onClose}
      customWidth={814}
      header={
        <>
          <p className="text-h2 text-neutral-98 dark:text-neutral-20">
            {t('components.ui.createProductModal.title')}
          </p>
          <GatingInfoBox />
          <p className="text-body2 text-neutral-70 dark:text-neutral-40">
            {t('components.ui.createProductModal.subtitle')}
          </p>
        </>
      }
    >
      <Form schema={schema} mode="onChange" onSubmit={e => e.preventDefault()}>
        {({ Field, register, getValues, watch, setValue, trigger }) => {
          const { productNameMaxLength, productSlugMaxLength } = appDefaults.productsSettings

          const watchProductName = watch('productName')
          const watchProductSlug = watch('productSlug')

          const defaultNameInvalid = !isDefaultValueValid(watchProductName, defaultProductName, productNameMaxLength)
          const defaultSlugInvalid = !isDefaultValueValid(watchProductSlug, defaultProductSlug, productSlugMaxLength)

          const handleSubmit = async () => {
            const isValid = await trigger()
            if (!isValid || nameExists || slugStatus?.exists || slugStatus?.reserved || disabled) {
              return
            }
            fetcher.submit(
              {
                ...getValues(),
                ...(templateId ? { templateId } : {})
              },
              {
                method: 'POST',
                action: duplicatedProduct && id ? ACTION.DUPLICATE_PRODUCT({ duplicatedProductId: id }) : ACTION.ADD_PRODUCT()
              })
          }

          return (
            <section className={clsx('flex flex-col gap-4', className)}>
              <Field
                name="productName"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const productName = event.target.value
                  if (!productName || productName.length < appDefaults.productsSettings.productNameMinLength) {
                    setNameExists(false)
                    setDisabled(true)
                    if (!slugManuallyEdited.current) {
                      const generatedSlug = slugify(productName, { separator: '-' })
                      setValue('productSlug', generatedSlug, { shouldValidate: true, shouldDirty: true })
                    }
                    return
                  }
                  nameFetcher.load(`/services/api/product?productname=${productName}`)

                  if (!slugManuallyEdited.current) {
                    const generatedSlug = slugify(productName, { separator: '-' })
                    setValue('productSlug', generatedSlug, { shouldValidate: true, shouldDirty: true })
                    if (generatedSlug && generatedSlug.length >= appDefaults.productsSettings.productSlugMinLength) {
                      setSlugStatus({ exists: false, loading: true })
                      slugFetcher.load(`/services/api/product?productslug=${generatedSlug}`)
                    }
                  }
                }}
                value={defaultProductName}
              >
                {({ Input, Label, errors, Errors }) =>
                  <>
                    <Label>
                      {t('components.ui.createProductModal.inputs.name.label')}
                      <span className="text-error-30" aria-hidden="true">*</span>
                    </Label>

                    <Input
                      data-testid="product-name-input"
                      placeholder={t('components.ui.createProductModal.inputs.name.placeholder')}
                      aria-invalid={nameExists || Boolean(errors) || defaultNameInvalid}
                    />
                    {(nameExists && !errors) && <Error>{t('components.ui.createProductModal.inputs.name.errors.exist')}</Error>}
                    {defaultNameInvalid && !errors &&
                      <Error>{t('components.ui.createProductModal.inputs.name.errors.maxLength', { maxLength: productNameMaxLength })}</Error>}
                    <Errors />
                  </>
                }
              </Field>
              <Field
                name="productSlug"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const productSlug = event.target.value
                  slugManuallyEdited.current = true
                  if (!productSlug || productSlug.length < appDefaults.productsSettings.productSlugMinLength) {
                    setDisabled(true)
                    setSlugStatus({ exists: false, loading: false })
                    return
                  }
                  setSlugStatus({ exists: false, loading: true })
                  slugFetcher.load(`/services/api/product?productslug=${productSlug}`)
                }}
                value={defaultProductSlug}
              >
                {({ Errors, errors, Label }) => (
                  <>
                    <Label>
                      {t('components.ui.createProductModal.inputs.slug.label')}
                      <span className="text-error-30" aria-hidden="true">*</span>
                    </Label>
                    <InputSplit
                      {...register('productSlug')}
                      data-testid="product-slug-input"
                      name="productSlug"
                      left={hostname}
                      placeholder={t('components.ui.createProductModal.inputs.slug.placeholder')}
                      error={Boolean(errors) || slugStatus?.exists || slugStatus?.reserved || defaultSlugInvalid}
                    />
                    {errors ? <Errors /> :
                      (defaultSlugInvalid ? <Error>{t('components.ui.createProductModal.inputs.name.errors.maxLength',
                        { maxLength: productSlugMaxLength })}</Error> : getFetcherError())}
                  </>
                )}
              </Field>
              {createNewProductMode &&
                <SelectTemplate initialSelectedId={initialTemplateId} templates={templatesItems || []} setFormValue={setTemplateId} />
              }
              {duplicatedProduct &&
                <Notification type={NotificationType.INFO}>
                  {t('components.ui.createProductModal.duplicateNotification')}
                </Notification>}
              <div className="flex justify-end gap-2">
                <SecondaryButton
                  onClick={onClose}
                  size="m"
                >
                  {t('components.ui.createProductModal.buttons.cancel.label')}
                </SecondaryButton>

                <PrimaryButton
                  data-testid="create-product-button"
                  type="button"
                  loading={loading}
                  onClick={handleSubmit}
                  size="m"
                  wide
                >
                  {t('components.ui.createProductModal.buttons.confirm.label')}
                </PrimaryButton>
              </div>
            </section>
          )
        }}
      </Form>
    </ModalDialog>
  )
}

export default CreateProductModal
